/**
 * TenantBoundary — the structural lock that replaces the old
 * TenantSessionGuard / UrlTenantKeeper pair.
 *
 * Mounted as the layout element for every `/:tenantSlug/*` route. Its
 * <Outlet /> only renders when the user IS authenticated AND the URL's
 * tenant slug matches the user's company surface. EVERY role is
 * locked to their own tenant — including super_admin. Super_admin
 * does platform-management via the editor at /<own>/super-admin/...,
 * which targets other tenants by ID without ever changing the URL
 * tenant context.
 *
 * Three exit paths:
 *   1. Not authenticated      → redirect to `/<slug>/login` (branded login).
 *   2. URL slug = own company → render the page (<Outlet />).
 *   3. Slug mismatch           → defer to <TenantMismatchHandler />,
 *      which decides "redirect to /<canonical>/<rest>" (if the slug
 *      belongs to the same company subtree — i.e. the user typed a
 *      sub-tenant code) vs "logout + default branded login" (a real
 *      cross-tenant jump).
 *
 * Because every protected page is <Outlet />-nested under this
 * element, the wrong-tenant render path is **structurally unreachable**.
 * No useEffect, no listener, no race.
 */

import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useAuth } from '@modules/auth';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { JourneyProvider, JourneyRoot } from '@modules/journey';
import { TenantMismatchHandler } from './TenantMismatchHandler';
import { buildTenantLoginPath, loginPathForHost } from './paths';

export function TenantBoundary() {
  const { tenantSlug = '' } = useParams<{ tenantSlug?: string }>();
  const slug = tenantSlug.toLowerCase();
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to={buildTenantLoginPath(slug)} replace />;

  const canonical = (user?.company_tenant_code || user?.tenant_code || '').toLowerCase();
  // loginPathForHost() (not DEFAULT_LOGIN_PATH): on a vanity host the
  // platform slug would pass through the slug-hiding history unstripped
  // and leak into the visible URL.
  if (!canonical) return <Navigate to={loginPathForHost()} replace />;
  if (slug !== canonical) return <TenantMismatchHandler typedSlug={slug} canonicalSlug={canonical} />;

  // Journey onboarding wraps every tenant route so its dock, locks and
  // detection listeners survive navigation. Inert (children-only) unless
  // modules.journey is on AND the user is in the v1 audience — see
  // JourneyProvider for the gating.
  return (
    <JourneyProvider>
      <Outlet />
      <JourneyRoot />
    </JourneyProvider>
  );
}
